import api from 'api';
import {useModal} from 'common/hooks/useModal';
import {usePostApi} from 'common/hooks/usePostApi';
import {ConfirmationDialog} from 'components/dialogs/ConfirmationDialog';
import React, {memo} from 'react';
import {useTranslation} from 'react-i18next';
import {useToasts} from 'features/notifications/useToasts';
import {IBackupRequest} from 'types/diplomat/backup/BackupRequest';
import {BackupLocationDialog} from './BackupLocationDialog';
import BackupSelectionDialog, {IBackupSelectionFormType} from './BackupSelectionDialog';
import BackupTypeChooser from './BackupTypeChooser';
import {FormOnSubmitHelpers} from 'components/hook-form/Formik';
import {useSelector} from 'react-redux';
import {licenseSelectors} from 'redux/license';
import {DiplomatFeature} from 'types/DiplomatFeature';

type Props = {
   onClose: () => void;
};

const BackupDialog = memo(({onClose}: Props) => {
   const {t} = useTranslation();
   const {open: chooserOpen, handleOpen: openChooser, handleClose: _handleChooserClose} = useModal(
      false
   );
   const {addToast} = useToasts();
   const [isFull, setFull] = React.useState<boolean | undefined>();
   const isFeatureSupported = useSelector(licenseSelectors.isFeatureSupported);

   React.useEffect(() => {
      if (isFeatureSupported(DiplomatFeature.SELECTIVE_MERGE)) {
         openChooser();
      } else {
         setFull(true);
      }
   }, []);

   const handleChooserClose = React.useCallback(
      (isFull?: boolean) => {
         if (isFull !== undefined && typeof isFull === 'boolean') {
            setFull(isFull);
         } else {
            onClose();
         }
         _handleChooserClose();
      },
      [_handleChooserClose, onClose]
   );

   const {open: isConfirmationOpened, handleOpen: openConfirmation} = useModal(false);
   const options = React.useMemo(
      () => ({
         onSuccess: (resp: any) => {
            addToast(t('Transaction datastore backed up to \n{{path}}', {path: resp.result}), {
               appearance: 'success',
            });
            onClose();
         },
         onFailure: (resp: any) => {
            // console.debug(resp);
            const e = JSON.stringify(resp);
            if (e.indexOf('File already exists') >= 0) {
               openConfirmation();
            }
         },
      }),
      [addToast, onClose, openConfirmation, t]
   );
   const {request} = usePostApi(api.backup, options);

   const [backupLocation, setBackupLocation] = React.useState<IBackupRequest | undefined>();
   const handleLocation = React.useCallback(
      (values?: IBackupRequest, helpers?: FormOnSubmitHelpers<IBackupRequest>) => {
         if (!values || !values.backupFilename) {
            onClose();
            return;
         }

         console.debug(values);
         setBackupLocation(values);
         if (isFull) {
            request(JSON.stringify(values)).then(() => helpers?.setSubmitting(false));
         } else {
            // openSelection();
         }
      },
      [isFull, onClose, request]
   );

   const handleSelection = React.useCallback(
      (
         values?: IBackupSelectionFormType,
         helpers?: FormOnSubmitHelpers<IBackupSelectionFormType>
      ) => {
         console.debug(values);
         if (values && values.transactionNames) {
            const transactionNames = values.transactionNames.map((v) => v.name);
            const data = {...backupLocation, transactionNames} as IBackupRequest;
            setBackupLocation(data);
            request(JSON.stringify(data)).then(() => helpers?.setSubmitting(false));
         } else {
            onClose();
            // openSelection();
         }
      },
      [backupLocation, request, onClose]
   );

   const handleOverwrite = React.useCallback(() => {
      const data = {...backupLocation, overwrite: true};
      request(JSON.stringify(data));
   }, [backupLocation, request]);

   return (
      <>
         {chooserOpen ? <BackupTypeChooser onClose={handleChooserClose} /> : null}
         {isFull !== undefined ? (
            <BackupLocationDialog onClose={handleLocation} isFull={isFull} />
         ) : null}
         {backupLocation && isFull === false ? (
            <BackupSelectionDialog onClose={handleSelection} />
         ) : null}

         {isConfirmationOpened ? (
            <ConfirmationDialog
               title={t('Backup File Already Exists')}
               content={t('Do you want to replace it?')}
               onOk={handleOverwrite}
               onCancel={onClose}
            />
         ) : null}
      </>
   );
});

export default BackupDialog;
